import { Link } from "react-router";
import { readJson } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import VideoEmbed from "../components/VideoEmbed/VideoEmbed.jsx";
import { TestimonialRow } from "../components/TestimonialGrid/TestimonialGrid.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./bim-virtual-tour.module.css";

const PAGE = {
  title: "Virtual Tour",
  description:
    "Guided online tours of your project for public involvement: the public explores the design from any device, at their own pace.",
  path: "/123bim/virtual-tour",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "3D Visualization", path: "/3d-visualization" },
  { name: "Virtual Tour", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/site/123bim/tour-gateway",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

export async function loader() {
  // A public outreach manager on what the public understood once they could see it.
  const quote = readJson("testimonials").find((t) => t.author === "Brian Bollas") || null;
  return { quote };
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["how", "proof"];
const surface = surfaceFor(LIGHT_SECTIONS);

const STEPS = [
  {
    title: "We place the viewpoints",
    text: "At the corners, driveways, parks and approaches that matter to the people who live there, each one a view of the proposed design you can look around.",
  },
  {
    title: "It goes where people already look",
    text: "A link on the project website and social media, a QR code in the brochure, a touch screen at the open house.",
  },
  {
    title: "They compare it in their own time",
    text: "Existing against proposed, or each construction stage, from the same viewpoint, on a phone, a tablet or a computer, with nothing to install.",
  },
];

export default function BimVirtualTour({ loaderData }) {
  const { quote } = loaderData;
  return (
    <>
      <section className={styles.hero} aria-labelledby="tour-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="tour-title" className={styles.heroTitle}>
              Let the public stand where the project will be
            </h1>
            <p className={styles.heroLead}>
              Virtual Tour puts the proposed design online as photorealistic viewpoints that
              residents move between at their own pace, comparing it with what is there today. It
              runs in any browser, on any device, with nothing to install.
            </p>
            <div className={styles.heroActions}>
              <Link to="/demo" className="btn btn-primary">
                Request a demo
              </Link>
              <a href="#how" className={styles.heroLink}>
                See how a tour works
              </a>
            </div>
            <p className={styles.heroNote}>
              Tens of thousands of visitors have used it to see how a project would change their
              neighborhood.
            </p>
          </div>
          <div className={styles.heroMedia}>
            <Picture
              src="/images/site/123bim/tour-gateway"
              alt="A virtual tour stop at a proposed interchange, with the viewpoints along the bottom"
              ratio="16 / 10"
              widths={[480, 960, 1600]}
              fallbackWidth={960}
              sizes="(min-width: 64rem) 52vw, 100vw"
              priority
              className={styles.heroImage}
            />
            <p className={styles.heroChip}>Runs in any browser</p>
          </div>
        </div>
      </section>

      <section
        className="section"
        id="how"
        data-surface={surface("how")}
        aria-labelledby="how-title"
      >
        <div className="container">
          <SectionHeader
            id="how-title"
            title="How a tour reaches the public"
            aside="One link, wherever a project already talks to people."
          />
          <ol className={styles.steps}>
            {STEPS.map((step, i) => (
              <li key={step.title}>
                <span className={styles.stepNo} aria-hidden="true">
                  {i + 1}
                </span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section" aria-labelledby="video-title">
        <div className={`container ${styles.video}`}>
          <div className={styles.videoText}>
            <h2 id="video-title">On the local news</h2>
            <p>
              WTNH News 8 on an open planning session for the I-84 Hartford project, one of the
              corridors RDV Systems modeled for the Connecticut Department of Transportation.
            </p>
            <Link to="/projects/i-84-hartford-3d-visualization" className={styles.heroLink}>
              The I-84 Hartford project
            </Link>
          </div>
          {/* The broadcast's own frame as the poster: the 3D model on air, under News 8's banner. */}
          <figure className={styles.news}>
            <VideoEmbed
              video="youtube:xlWUDXA5Pmc"
              title="WTNH News 8: open planning session for the I-84 Hartford project"
              poster="/images/video-posters/youtube-xlwudxa5pmc"
            />
            <figcaption className={styles.newsChip}>As aired on WTNH News 8</figcaption>
          </figure>
        </div>
      </section>

      {quote ? (
        <section className="section" data-surface={surface("proof")} aria-label="What it did">
          <div className="container">
            <TestimonialRow item={quote} />
          </div>
        </section>
      ) : null}

      <CTABand
        variant="statement"
        image="/images/site/123bim/photo-morning"
        title="Plan a virtual tour for your next public meeting"
        text="Send a project model and get a link the public can tour from any device."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
